import {
    CheckIcon,
    EnvelopeIcon,
    MapPinIcon,
    TrashIcon,
    UserIcon,
    UserPlusIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { Tooltip } from '@/components/core/Tooltip';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector } from '@/services/angularjs-bridge';
import { restUrl } from '@/services/utils';

import { hidesContactActions } from './businessPartner';
import { DeleteContactDialog } from './DeleteContactDialog';
import { useMemberCommunities, useOrganizationSearch } from './organizationSearch.queries';
import {
    canDeleteSearchedOrganization,
    canInviteOrganization,
    communityLabel,
    SearchedOrganization,
} from './searchedOrganization';
import { SendEmailInvitationDialog } from './SendEmailInvitationDialog';
import { SendOrganizationInvitationDialog } from './SendOrganizationInvitationDialog';

interface OrganizationSearchProps {
    mode: 'search' | 'community';
}

const routeKeyword = () => {
    try {
        const query = getInjector().get('$routeParams').q;
        return typeof query === 'string' ? query : '';
    } catch {
        return '';
    }
};

const OrganizationSearch = ({ mode }: OrganizationSearchProps) => {
    const { t } = useTranslation();
    const { organization } = useOrganization();
    const communityMode = mode === 'community';
    const [keyword, setKeyword] = useState(routeKeyword);
    const [debouncedKeyword, setDebouncedKeyword] = useState(() => routeKeyword().trim());
    const [chosenCommunityId, setChosenCommunityId] = useState<number | undefined>();
    const [inviteOrganization, setInviteOrganization] = useState<SearchedOrganization | undefined>();
    const [deleteOrganization, setDeleteOrganization] = useState<SearchedOrganization | undefined>();
    const [sendEmailOpen, setSendEmailOpen] = useState(false);
    const communityManager = Boolean(organization?.communityManager);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedKeyword(keyword.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [keyword]);

    const communities = useMemberCommunities(communityMode);
    const communityOptions = useMemo(() => communities.data ?? [], [communities.data]);
    const selectedCommunity = communityOptions.find((item) => item.id === chosenCommunityId) ?? communityOptions[0];
    const communityId = communityMode ? selectedCommunity?.id : undefined;
    const waitingForCommunity = communityMode && (communities.isLoading || (communities.isSuccess && communityId == null && communityOptions.length > 0));
    const results = useOrganizationSearch({
        keyword: debouncedKeyword || undefined,
        communityId,
    }, !waitingForCommunity && (!communityMode || communities.isSuccess));

    const organizations = results.data ?? [];
    const communityScoped = communityId != null;

    return (
        <div id="searchBusinesses" className="tw-flex tw-flex-col tw-gap-4">
            {communityMode && communityOptions.length > 1 && (
                <label className="tw-flex tw-flex-col tw-gap-1 lg:tw-max-w-xl" htmlFor="selectCommunity">
                    <span className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                        {t('COMMUNITIES.CHOOSE_COMMUNITY')}
                    </span>
                    <Select
                        value={selectedCommunity == null ? undefined : String(selectedCommunity.id)}
                        onValueChange={(value) => setChosenCommunityId(Number(value))}
                    >
                        <SelectTrigger id="selectCommunity">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {communityOptions.map((item) => (
                                <SelectItem key={item.id} value={String(item.id)}>
                                    {communityLabel(item)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </label>
            )}
            {communityMode && communityOptions.length === 1 && (
                <p className="tw-m-0 tw-text-t-sm tw-font-medium tw-text-t-primary lg:tw-text-t-base">
                    {communityLabel(communityOptions[0])}
                </p>
            )}

            <div className="tw-flex tw-flex-col tw-gap-1">
                <label
                    className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base"
                    htmlFor="search-organization-input"
                >
                    {t('SEARCH_BUSINESSES')}
                </label>
                <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center">
                    <div className="tw-min-w-0 tw-flex-1">
                        <Input
                            id="search-organization-input"
                            value={keyword}
                            placeholder={t('ORGANIZATION_SEARCH_PLACEHOLDER')}
                            onChange={(event) => setKeyword(event.target.value)}
                        />
                    </div>
                    {communityManager && (
                        <Button
                            id="send-email-invitation-btn"
                            className="tw-h-9 tw-shrink-0"
                            type="button"
                            onClick={() => setSendEmailOpen(true)}
                        >
                            {t('SEND_EMAIL_INVITATION')}
                        </Button>
                    )}
                </div>
            </div>

            {(communities.isLoading || results.isLoading) && <Loader />}

            {results.isSuccess && organizations.length === 0 && (
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-10">
                    <span className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-fill-secondary lg:tw-h-20 lg:tw-w-20">
                        <Icon icon={UserIcon} className="tw-h-8 tw-w-8 lg:tw-h-10 lg:tw-w-10" variant="dark" />
                    </span>
                    <h2 className="tw-text-center tw-text-t-base tw-font-bold tw-text-h-primary lg:tw-text-t-lg">
                        {t('NO_MATCHES')}
                    </h2>
                </div>
            )}

            {organizations.length > 0 && (
                <table id="results-table" className="tw-w-full">
                    <tbody>
                        {organizations.map((item, index) => {
                            const showActions = !hidesContactActions(organization?.id, item.id);
                            const showInvite = canInviteOrganization({
                                communityManager,
                                communityScoped,
                                contact: item.contactData,
                            });
                            const showDelete = canDeleteSearchedOrganization(
                                communityManager,
                                item.contactData?.invitationType,
                            );
                            return (
                                <Fragment key={item.id}>
                                    {index > 0 && (
                                        <tr>
                                            <td className="tw-p-0">
                                                <div className="tw-h-px tw-w-full tw-bg-gray-200" />
                                            </td>
                                        </tr>
                                    )}
                                    <tr className="invitation invitation-row">
                                        <td className="tw-p-3">
                                            <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-gap-4">
                                                <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-fill-secondary">
                                                    {item.hasImage ? (
                                                        <img
                                                            src={restUrl(`organizations/${item.id}/image`)}
                                                            alt={item.name ?? ''}
                                                            className="tw-h-full tw-w-full tw-object-cover"
                                                        />
                                                    ) : (
                                                        <Icon icon={UserIcon} className="tw-h-5 tw-w-5" variant="secondary" />
                                                    )}
                                                </div>
                                                <div className="tw-grid tw-min-w-0 tw-flex-1 tw-grid-cols-1 tw-gap-2 lg:tw-grid-cols-2">
                                                    <div className="tw-min-w-0">
                                                        <div className="tw-flex tw-items-center tw-gap-1">
                                                            <span className="tw-break-words tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                                                                {item.name}
                                                            </span>
                                                            {item.matchingInviteeDataModel && (
                                                                <Tooltip popupContent={t('CONTACTS.MATCHING_INVITEE_DATA_MODEL')}>
                                                                    <Icon
                                                                        icon={CheckIcon}
                                                                        className="tw-h-4 tw-w-4 lg:tw-h-5 lg:tw-w-5"
                                                                        variant="success"
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                        {item.gln && (
                                                            <div className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{item.gln}</div>
                                                        )}
                                                        {item.contactData?.contactRole && (
                                                            <div className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                                                {t(item.contactData.contactRole)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                                        <div className="tw-flex tw-items-start tw-gap-2">
                                                            <Icon icon={MapPinIcon} className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 lg:tw-h-5 lg:tw-w-5" variant="secondary" />
                                                            <span className="tw-break-words">{item.address}</span>
                                                        </div>
                                                        <div className="tw-flex tw-items-start tw-gap-2">
                                                            <Icon icon={EnvelopeIcon} className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 lg:tw-h-5 lg:tw-w-5" variant="secondary" />
                                                            <span className="tw-break-all">{item.email}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {showActions && (
                                                    <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-end">
                                                        <IconButton
                                                            icon={EnvelopeIcon}
                                                            size="md"
                                                            className="tw-icon-primary"
                                                            title={t('CONTACTS.SEND_EMAIL')}
                                                            aria-label={t('CONTACTS.SEND_EMAIL')}
                                                            onClick={() => {
                                                                window.location.href = `mailto:${item.email ?? ''}`;
                                                            }}
                                                        />
                                                        {showInvite && (
                                                            <IconButton
                                                                id="sendInvitation"
                                                                icon={UserPlusIcon}
                                                                size="md"
                                                                className="tw-icon-primary"
                                                                title={t('INVITATION.TITLE_INVITATION')}
                                                                aria-label={t('INVITATION.TITLE_INVITATION')}
                                                                onClick={() => setInviteOrganization(item)}
                                                            />
                                                        )}
                                                        {showDelete && (
                                                            <IconButton
                                                                icon={TrashIcon}
                                                                size="md"
                                                                className="tw-icon-primary"
                                                                title={t('CONTACTS.DELETE_CONTACT')}
                                                                aria-label={t('CONTACTS.DELETE_CONTACT')}
                                                                onClick={() => setDeleteOrganization(item)}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}

            <SendOrganizationInvitationDialog
                open={inviteOrganization != null}
                organization={inviteOrganization}
                onClose={() => setInviteOrganization(undefined)}
            />
            <DeleteContactDialog
                open={deleteOrganization != null}
                contact={deleteOrganization ? { id: deleteOrganization.id, name: deleteOrganization.name } : undefined}
                onClose={() => setDeleteOrganization(undefined)}
            />
            <SendEmailInvitationDialog open={sendEmailOpen} onClose={() => setSendEmailOpen(false)} />
        </div>
    );
};

export { OrganizationSearch };
